import { useState } from 'react';

import {
    InlineSettingCard,
    type InlineSettingControl,
    type InlineSettingStatus,
} from '@apps/web/components/phoebe_chat_view/tool_calls/inline_setting_card';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiBodyType } from '@libraries/typescript/api_client';

/**
 * Renders the agent's `surface_organization_setting` tool result as a live
 * inline control. The tool payload carries the render metadata (kind, title,
 * options, bounds, override rule) from the backend settings catalog; this
 * component maps it onto `InlineSettingCard` and routes the user's edit
 * through the same PUT endpoint the Settings page uses (agent ≤ UI: the
 * user is the actor, and API authorization applies on save exactly as on
 * the Settings page).
 */

const NAMESPACE_SAVE_URLS = {
    general: '/organizations/{organization_id}/general-settings',
    shift_confirmation:
        '/organizations/{organization_id}/shift-confirmation-settings',
    callout: '/organizations/{organization_id}/callout-settings',
    activity_feed: '/organizations/{organization_id}/activity-feed-settings',
} as const;

type SettingsNamespace = keyof typeof NAMESPACE_SAVE_URLS;

function isSettingsNamespace(value: string): value is SettingsNamespace {
    return Object.prototype.hasOwnProperty.call(NAMESPACE_SAVE_URLS, value);
}

type OverrideRule = {
    readonly key: string;
    readonly value: unknown;
    readonly text: string;
};

// Transcripts from before the settings catalog carry a bare
// general-settings key and no render metadata; map those payloads to their
// namespaced form so old chats keep rendering. Titles are the ones the
// original card hardcoded.
const LEGACY_GENERAL_TITLES: Record<string, string> = {
    auto_send_all_replies: 'Auto-send all replies',
    auto_send_positive_shift_confirmation_replies:
        'Auto-send shift confirmation acceptance replies',
    auto_send_negative_shift_confirmation_replies:
        'Auto-send shift confirmation decline replies',
    auto_send_unclear_shift_confirmation_replies:
        'Auto-send unclear reply clarification texts',
    mark_positive_shift_confirmation_messages_as_read:
        'Mark positive shift confirmations as read',
    auto_send_callout_decline_response: 'Auto-send Shift Outreach responses',
    mark_decline_outreach_messages_as_read: 'Mark outreach declines as read',
};

const LEGACY_AUTO_SEND_ALL_OVERRIDE: OverrideRule = {
    key: 'general.auto_send_all_replies',
    value: true,
    text: 'Overridden by Auto-send all replies.',
};

const LEGACY_OVERRIDDEN_FIELDS = new Set([
    'auto_send_positive_shift_confirmation_replies',
    'auto_send_negative_shift_confirmation_replies',
    'auto_send_unclear_shift_confirmation_replies',
    'auto_send_callout_decline_response',
]);

// Client-side mirror of the backend SETTINGS_CATALOG keys. Defense in depth:
// the card builds the PUT body from the payload's field name, so this
// allowlist stops a crafted/hallucinated tool payload from routing an
// off-catalog field (e.g. an admin-tier column on the same Update schema)
// through a save. Fail-safe: a key the backend adds but this set lacks simply
// won't render (a functional gap caught in testing), never a security hole.
// Keep in sync with libraries/python/phoebe_event_agent/tools/settings_catalog.py.
const SURFACEABLE_SETTING_KEYS: ReadonlySet<string> = new Set([
    'general.auto_send_all_replies',
    'general.auto_send_positive_shift_confirmation_replies',
    'general.auto_send_negative_shift_confirmation_replies',
    'general.auto_send_unclear_shift_confirmation_replies',
    'general.auto_send_callout_decline_response',
    'general.mark_positive_shift_confirmation_messages_as_read',
    'general.mark_decline_outreach_messages_as_read',
    'general.enable_clock_reminders_in_test_mode',
    'general.work_week_start_day',
    'general.client_call_phone_preference',
    'shift_confirmation.enabled',
    'shift_confirmation.enable_relative_dates',
    'callout.skip_busy_caregivers',
    'callout.auto_assign_first_responder_default',
    'callout.auto_group_default',
    'callout.default_shift_group_mode',
    'callout.recurring_shift_default_group_mode',
    'activity_feed.voice_call_note_emit_min_priority',
    'activity_feed.voice_call_note_emit_only_action_required',
    'activity_feed.callout_opened_enabled',
    'activity_feed.callout_closed_enabled',
    'activity_feed.callout_response_enabled',
    'activity_feed.outreach_status_update_enabled',
    'activity_feed.outreach_action_needed_enabled',
    'activity_feed.missed_clock_in_enabled',
    'activity_feed.missed_clock_out_enabled',
    'activity_feed.shift_cancellation_enabled',
    'activity_feed.after_hours_receptionist_enabled',
    'activity_feed.auto_address_enabled',
    'activity_feed.auto_address_after_days',
]);

export const SETTINGS_SURFACE_TOOL_NAMES = new Set<string>([
    'surface_organization_setting',
]);

type SettingOption = {
    readonly value: string;
    readonly label: string;
};

type ParsedSurfacePayload = {
    readonly namespace: SettingsNamespace;
    readonly field: string;
    readonly title: string;
    readonly description?: string;
    readonly kind: 'toggle' | 'select' | 'number';
    readonly options: ReadonlyArray<SettingOption>;
    readonly min?: number;
    readonly max?: number;
    readonly value: unknown;
    readonly overriddenBy: OverrideRule | null;
    // Org feature flag this setting's editor depends on. Re-checked at render
    // so a card surfaced while the feature was on goes inert if it's later
    // disabled (the Settings-page editor would be gone).
    readonly requiresFeature?: string;
};

function parseOptions(raw: unknown): ReadonlyArray<SettingOption> {
    if (!Array.isArray(raw)) {
        return [];
    }
    const options: Array<SettingOption> = [];
    for (const item of raw) {
        if (item === null || typeof item !== 'object') {
            continue;
        }
        const record: Record<string, unknown> = { ...item };
        if (
            typeof record['value'] === 'string' &&
            typeof record['label'] === 'string'
        ) {
            options.push({ value: record['value'], label: record['label'] });
        }
    }
    return options;
}

function parseOverrideRule(raw: unknown): OverrideRule | null {
    if (raw === null || raw === undefined || typeof raw !== 'object') {
        return null;
    }
    const record: Record<string, unknown> = { ...raw };
    if (
        typeof record['key'] !== 'string' ||
        typeof record['text'] !== 'string'
    ) {
        return null;
    }
    return {
        key: record['key'],
        value: record['value'],
        text: record['text'],
    };
}

function parseSurfacePayload(
    data: Record<string, unknown>,
): ParsedSurfacePayload | null {
    const settingKey = data['setting_key'];
    if (typeof settingKey !== 'string') {
        return null;
    }

    if (!settingKey.includes('.')) {
        // Legacy bare-key payload from a pre-catalog transcript. Own-property
        // check so inherited names ('constructor', 'toString', …) don't
        // resolve to a Function title.
        const title = Object.prototype.hasOwnProperty.call(
            LEGACY_GENERAL_TITLES,
            settingKey,
        )
            ? LEGACY_GENERAL_TITLES[settingKey]
            : undefined;
        if (title === undefined) {
            return null;
        }
        return {
            namespace: 'general',
            field: settingKey,
            title,
            kind: 'toggle',
            options: [],
            value: data['value'],
            overriddenBy: LEGACY_OVERRIDDEN_FIELDS.has(settingKey)
                ? LEGACY_AUTO_SEND_ALL_OVERRIDE
                : null,
        };
    }

    // Defense in depth: only render keys the backend catalog authorizes, so a
    // crafted payload can't route an off-catalog field into a save.
    if (!SURFACEABLE_SETTING_KEYS.has(settingKey)) {
        return null;
    }
    const [namespace, field] = settingKey.split('.', 2);
    const kind = data['kind'];
    const title = data['title'];
    if (
        namespace === undefined ||
        field === undefined ||
        field === '' ||
        !isSettingsNamespace(namespace) ||
        typeof title !== 'string' ||
        (kind !== 'toggle' && kind !== 'select' && kind !== 'number')
    ) {
        return null;
    }
    return {
        namespace,
        field,
        title,
        description:
            typeof data['description'] === 'string'
                ? data['description']
                : undefined,
        kind,
        options: parseOptions(data['options']),
        min: typeof data['min'] === 'number' ? data['min'] : undefined,
        max: typeof data['max'] === 'number' ? data['max'] : undefined,
        value: data['value'],
        overriddenBy: parseOverrideRule(data['overridden_by']),
        requiresFeature:
            typeof data['requires_feature'] === 'string'
                ? data['requires_feature']
                : undefined,
    };
}

function fieldValue(group: unknown, field: string): unknown {
    if (group === null || group === undefined || typeof group !== 'object') {
        return undefined;
    }
    // Own-property check so a field like 'constructor' doesn't read an
    // inherited Object.prototype member.
    if (!Object.prototype.hasOwnProperty.call(group, field)) {
        return undefined;
    }
    return (group as Record<string, unknown>)[field];
}

function readLiveValue(
    organization: ReturnType<typeof useOrganization>['data'],
    namespace: SettingsNamespace,
    field: string,
): unknown {
    switch (namespace) {
        case 'general':
            return fieldValue(organization?.general_settings, field);
        case 'shift_confirmation':
            return fieldValue(organization?.shift_confirmation_settings, field);
        case 'callout':
            return fieldValue(organization?.callout_settings, field);
        case 'activity_feed':
            return fieldValue(
                fieldValue(organization?.features, 'activity_feed_settings'),
                field,
            );
        default: {
            const exhaustive: never = namespace;
            throw new Error(`Unhandled namespace: ${String(exhaustive)}`);
        }
    }
}

function readLiveValueByKey(
    organization: ReturnType<typeof useOrganization>['data'],
    key: string,
): unknown {
    const [namespace, field] = key.split('.', 2);
    if (
        namespace === undefined ||
        field === undefined ||
        !isSettingsNamespace(namespace)
    ) {
        return undefined;
    }
    return readLiveValue(organization, namespace, field);
}

function buildControl(
    parsed: ParsedSurfacePayload,
    liveValue: unknown,
): InlineSettingControl | null {
    // Prefer the live SWR value (the card must reflect Settings-page edits)
    // and fall back to the value captured in the tool payload — needed for
    // JSONB-backed settings whose key may be absent from the org read.
    switch (parsed.kind) {
        case 'toggle': {
            const value =
                typeof liveValue === 'boolean'
                    ? liveValue
                    : typeof parsed.value === 'boolean'
                      ? parsed.value
                      : false;
            return { type: 'toggle', value };
        }
        case 'select': {
            const value =
                typeof liveValue === 'string'
                    ? liveValue
                    : typeof parsed.value === 'string'
                      ? parsed.value
                      : null;
            if (value === null || parsed.options.length === 0) {
                return null;
            }
            return { type: 'select', value, options: parsed.options };
        }
        case 'number': {
            const value =
                typeof liveValue === 'number'
                    ? liveValue
                    : typeof parsed.value === 'number'
                      ? parsed.value
                      : null;
            if (value === null) {
                return null;
            }
            return {
                type: 'number',
                value,
                min: parsed.min,
                max: parsed.max,
            };
        }
        default: {
            const exhaustive: never = parsed.kind;
            throw new Error(`Unhandled kind: ${String(exhaustive)}`);
        }
    }
}

async function saveSetting(
    organizationId: string,
    parsed: ParsedSurfacePayload,
    value: boolean | string | number,
): Promise<void> {
    const pathParams = { organization_id: organizationId };
    // The backend catalog derives every surfaceable field from the same
    // Update schemas that type these PUT bodies (an unknown field fails at
    // import time server-side), so this single-field body is always a valid
    // member of the endpoint's body type even though TypeScript cannot
    // prove a dynamic key.
    const body = { [parsed.field]: value };
    switch (parsed.namespace) {
        case 'general':
            await getApiClient().put({
                url: NAMESPACE_SAVE_URLS.general,
                pathParams,
                body: body as ApiBodyType<
                    typeof NAMESPACE_SAVE_URLS.general,
                    'put'
                >,
            });
            return;
        case 'shift_confirmation':
            await getApiClient().put({
                url: NAMESPACE_SAVE_URLS.shift_confirmation,
                pathParams,
                body: body as ApiBodyType<
                    typeof NAMESPACE_SAVE_URLS.shift_confirmation,
                    'put'
                >,
            });
            return;
        case 'callout':
            await getApiClient().put({
                url: NAMESPACE_SAVE_URLS.callout,
                pathParams,
                body: body as ApiBodyType<
                    typeof NAMESPACE_SAVE_URLS.callout,
                    'put'
                >,
            });
            return;
        case 'activity_feed':
            await getApiClient().put({
                url: NAMESPACE_SAVE_URLS.activity_feed,
                pathParams,
                body: body as ApiBodyType<
                    typeof NAMESPACE_SAVE_URLS.activity_feed,
                    'put'
                >,
            });
            return;
        default: {
            const exhaustive: never = parsed.namespace;
            throw new Error(`Unhandled namespace: ${String(exhaustive)}`);
        }
    }
}

export function SurfaceOrganizationSettingCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const organizationId = useOrganizationId();
    const { data: organization, mutate: mutateOrganization } = useOrganization(
        organizationId,
        { reason: 'sidebar-surface-organization-setting' },
    );
    const [status, setStatus] = useState<InlineSettingStatus>('idle');
    // Org-level kill switch for chat settings editing. A rendered card is
    // always live, so it must never outlive the feature — but the transcript
    // keeps a static note so turning the flag off doesn't silently erase the
    // record that the agent surfaced a setting.
    const chatSettingsEditingEnabled = useFeatureFlag(
        'chat_settings_editing_enabled',
        organization?.features,
    );

    const parsed = parseSurfacePayload(data);
    if (parsed === null) {
        return null;
    }
    if (!chatSettingsEditingEnabled) {
        // While the org (and with it the flag) is still loading, render
        // nothing rather than flashing the turned-off copy.
        if (!organization) {
            return null;
        }
        return (
            <p className="text-muted-foreground text-sm">
                Phoebe surfaced the “{parsed.title}” setting here. Editing
                settings from chat is turned off for this organization.
            </p>
        );
    }

    // Re-check the setting's feature gate against live org features. A card
    // surfaced while the feature was on must not stay editable after the
    // feature is disabled — the Settings-page editor would be gone, so an
    // edit here would breach agent ≤ UI. Mirror the backend's
    // `features.get(requires_feature)` check.
    if (parsed.requiresFeature !== undefined) {
        if (!organization) {
            return null;
        }
        if (!fieldValue(organization.features, parsed.requiresFeature)) {
            return (
                <p className="text-muted-foreground text-sm">
                    Phoebe surfaced the “{parsed.title}” setting here. It’s no
                    longer available for this organization.
                </p>
            );
        }
    }

    const liveValue = readLiveValue(
        organization,
        parsed.namespace,
        parsed.field,
    );
    const control = buildControl(parsed, liveValue);
    if (control === null) {
        return null;
    }

    // While the overriding setting holds its overriding value, this control
    // is inert; mirror the Settings page by disabling + annotating rather
    // than letting the user save a value that has no effect.
    const isOverridden =
        parsed.overriddenBy !== null &&
        readLiveValueByKey(organization, parsed.overriddenBy.key) ===
            parsed.overriddenBy.value;

    const handleValueChange = async (value: boolean | string | number) => {
        if (!organization?.id) {
            return;
        }
        setStatus('saving');
        try {
            await saveSetting(organization.id, parsed, value);
            await mutateOrganization();
            setStatus('updated');
        } catch (error) {
            console.error(
                'Failed to update organization setting from chat:',
                error,
            );
            setStatus('error');
        }
    };

    return (
        <InlineSettingCard
            title={parsed.title}
            description={
                isOverridden && parsed.overriddenBy !== null
                    ? parsed.overriddenBy.text
                    : parsed.description
            }
            control={control}
            status={status}
            disabled={!organization || isOverridden}
            onValueChange={handleValueChange}
        />
    );
}
